import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 3,
  orderby: "name",
  order: "asc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
function CollectionsGrid() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className={`h-[480px] border border-border bg-card/40 backdrop-blur-sm animate-pulse rounded-lg ${i === 1 ? "lg:mt-16" : ""}`} />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-start gap-4 border border-border bg-card/40 backdrop-blur-sm p-8 rounded-lg">
        <p className="text-sm text-muted-foreground font-default">
          Unable to load categories at the moment.
        </p>
        <Button variant="outline" onClick={refetch} className="border-border text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg">
          Retry
        </Button>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="border border-border bg-card/40 backdrop-blur-sm p-12 text-center rounded-lg">
        <p className="text-muted-foreground font-default">
          No categories to display yet.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">
      {terms.map((term, i) => {
      const indexLabel = String(i + 1).padStart(2, "0");
      const offsetClass = i === 1 ? "lg:mt-16" : "";
      const getCtaText = (name: string) => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes("newborn")) return "Explore Newborn Guides";
        if (lowerName.includes("travel")) return "Discover Travel Tips";
        if (lowerName.includes("childcare")) return "Learn Childcare Practices";
        return "View Collection";
      };
      const getEmotionalAnchor = (name: string) => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes("newborn")) return "Prioritize newborn hygiene with a gentle, non-toxic mist that creates a safe sanctuary for your little one's sensitive environment.";
        if (lowerName.includes("travel")) return "Master travel hygiene from terminal to taxi; maintain peace of mind with portable protection that fits your mobile lifestyle.";
        if (lowerName.includes("childcare")) return "Elevate childcare hygiene standards by bridging the gap between home and school with a gentle ritual for every shared surface.";
        return "A soft, effective boundary for your family's most important daily transitions and hygiene routines.";
      };
      const cardContent = <div className={`group relative flex h-[520px] flex-col justify-between border border-border bg-card/40 p-8 backdrop-blur-sm shadow-md transition-all duration-500 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg rounded-lg`}>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="font-default text-xs uppercase tracking-widest text-primary font-semibold">
                  {indexLabel} / {term.name}
                </span>
              </div>
              <div className="h-px w-full bg-border transition-colors duration-500 group-hover:bg-primary/60" />
            </div>

            <div className="relative flex h-[200px] items-center justify-center overflow-hidden">
              {term.image_url ? <img src={term.image_url} alt={`${term.name} collection – gentle, family-friendly hygiene solution`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-all duration-500 group-hover:scale-105 rounded-lg" /> : <div className="flex h-full w-full items-center justify-center border border-border/50 bg-background/40 rounded-lg">
                  <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                    {term.name}
                  </span>
                </div>}
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h3 className="font-default text-2xl font-medium uppercase tracking-tight text-foreground">
                  {term.name}
                </h3>
                <p className="font-default text-sm leading-relaxed text-muted-foreground line-clamp-2" dangerouslySetInnerHTML={{
              __html: term.description || getEmotionalAnchor(term.name)
            }} />
              </div>
              
              <div className="flex items-center justify-between gap-4">
                <Button variant="outline" className="flex-1 justify-between border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground rounded-lg text-xs uppercase tracking-wider font-semibold h-10">
                  {getCtaText(term.name)}
                  <ArrowRight className="h-3 w-3" />
                </Button>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-border text-foreground transition-all duration-500 group-hover:translate-x-1 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground rounded-lg">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </div>;
      if (term.link) {
        return <Link key={term.id} to={term.link} data-index={i} className={`block ${offsetClass}`}>
              {cardContent}
            </Link>;
      }
      return <div key={term.id} data-index={i} className={offsetClass}>
            {cardContent}
          </div>;
    })}
    </div>;
}
export default function Collections() {
  return <section data-section-id="3594" id="hygiene-for-every-journey" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-4 sm:px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 mb-20 lg:mb-28">
          <div className="flex flex-col gap-6">
            <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl font-medium uppercase leading-[1.1] tracking-tighter text-foreground">
              Our Collections:
              <br />
              <span className="italic text-primary">
                Hygiene for Every Journey
              </span>
            </h2>
          </div>

          <div className="flex flex-col items-start justify-end gap-6 lg:items-end lg:text-right">
            <div className="flex flex-col gap-4 max-w-sm">
              <p className="font-default text-lg leading-relaxed text-muted-foreground">
                From the nursery to the terminal, our mist provides gentle, 
                non-clinical <span className="italic text-primary font-medium">hygiene for every journey</span>.
              </p>
              <p className="font-default text-sm leading-relaxed text-muted-foreground/80">
                Our curated solutions are thoughtfully designed to address the unique hygiene needs of different life stages, ensuring safety without compromise.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-2 lg:justify-end">
                <Link to="/NewbornParentsPage" className="text-xs uppercase tracking-widest text-primary/70 hover:text-primary transition-colors">Newborn</Link>
                <Link to="/TravelHygienePage" className="text-xs uppercase tracking-widest text-primary/70 hover:text-primary transition-colors">Travel</Link>
                <Link to="/ChildcareCentersPage" className="text-xs uppercase tracking-widest text-primary/70 hover:text-primary transition-colors">Childcare</Link>
              </div>
            </div>
            <Link to="/ShopPage" className="group inline-flex items-center gap-3 font-default text-xs uppercase tracking-[0.25em] text-primary hover:opacity-80 transition-opacity">
              Shop All Products
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <EcommerceTermsProvider wp_term_query={wp_term_query} className="curated-collections-grid">
          <CollectionsGrid />
        </EcommerceTermsProvider>
      </div>
    </section>;
}